import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Pencil, Trash2, LogOut, ExternalLink } from "lucide-react";

const AdminDashboard = () => {
    const [recipes, setRecipes] = useState([]);
    const [loading, setLoading] = useState(true);
    const { logout, user } = useAuth();
    const navigate = useNavigate();

    const load = () => {
        setLoading(true);
        api.get("/recipes")
            .then((r) => setRecipes(r.data))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
    }, []);

    const onDelete = async (id, title) => {
        if (!window.confirm(`Delete "${title}"? This can't be undone.`)) return;
        try {
            await api.delete(`/recipes/${id}`);
            load();
        } catch (e) {
            alert("Delete failed: " + (e?.response?.data?.detail || e.message));
        }
    };

    const onLogout = async () => {
        await logout();
        navigate("/");
    };

    return (
        <div data-testid="admin-dashboard" className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-24">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
                <div>
                    <p className="font-script text-[#C86F53] text-2xl leading-none">kitchen control —</p>
                    <h1 className="font-serif text-4xl md:text-5xl text-stone-900 mt-1">
                        Your recipes
                    </h1>
                    <p className="text-stone-500 mt-2">
                        Signed in as <span className="text-stone-700">{user?.email}</span>
                    </p>
                </div>
                <div className="flex gap-3">
                    <Link
                        to="/admin/new"
                        data-testid="admin-new-recipe"
                        className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#5B6E4C] text-[#FAF8F5] hover:bg-[#42533A] hl-btn-lift text-sm uppercase tracking-widest"
                    >
                        <Plus className="w-4 h-4" /> New recipe
                    </Link>
                    <button
                        data-testid="admin-logout"
                        onClick={onLogout}
                        className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-stone-300 text-stone-700 hover:bg-stone-100 hl-btn-lift text-sm uppercase tracking-widest"
                    >
                        <LogOut className="w-4 h-4" /> Log out
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
                <table data-testid="admin-recipes-table" className="w-full text-left">
                    <thead className="bg-[#F4F1E9] text-xs uppercase tracking-widest text-stone-600">
                        <tr>
                            <th className="py-4 px-5">Recipe</th>
                            <th className="py-4 px-5 hidden md:table-cell">Origin</th>
                            <th className="py-4 px-5 hidden md:table-cell">Updated</th>
                            <th className="py-4 px-5 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={4} className="py-10 text-center text-stone-500">Loading...</td></tr>
                        ) : recipes.length === 0 ? (
                            <tr><td colSpan={4} data-testid="admin-no-recipes" className="py-10 text-center text-stone-500 italic">
                                No recipes yet. Start the kitchen with a new one.
                            </td></tr>
                        ) : recipes.map((r) => (
                            <tr
                                key={r.id}
                                data-testid={`admin-row-${r.slug}`}
                                className="border-t border-stone-100 hover:bg-[#FAF8F5]/70 transition-colors"
                            >
                                <td className="py-4 px-5">
                                    <div className="flex items-center gap-4">
                                        <img
                                            src={r.hero_image}
                                            alt=""
                                            className="w-14 h-14 rounded-lg object-cover"
                                        />
                                        <div>
                                            <p className="font-serif text-lg text-stone-900">{r.title}</p>
                                            <p className="text-xs text-stone-500">/{r.slug}</p>
                                        </div>
                                    </div>
                                </td>
                                <td className="py-4 px-5 hidden md:table-cell text-stone-700">{r.origin}</td>
                                <td className="py-4 px-5 hidden md:table-cell text-stone-500 text-sm">
                                    {new Date(r.updated_at).toLocaleDateString()}
                                </td>
                                <td className="py-4 px-5">
                                    <div className="flex items-center justify-end gap-2">
                                        <Link
                                            to={`/recipes/${r.slug}`}
                                            target="_blank"
                                            title="View public page"
                                            className="p-2 rounded-lg hover:bg-stone-100 text-stone-600"
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                        </Link>
                                        <Link
                                            to={`/admin/edit/${r.id}`}
                                            data-testid={`admin-edit-${r.slug}`}
                                            title="Edit"
                                            className="p-2 rounded-lg hover:bg-stone-100 text-[#5B6E4C]"
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </Link>
                                        <button
                                            data-testid={`admin-delete-${r.slug}`}
                                            onClick={() => onDelete(r.id, r.title)}
                                            title="Delete"
                                            className="p-2 rounded-lg hover:bg-[#C86F53]/10 text-[#C86F53]"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminDashboard;
